import {
  forwardRef,
  type HTMLAttributes,
  useImperativeHandle,
  useRef,
} from 'react';
import { type CurrencyType } from '@common/types/user';
import {
  H1,
  StringMain,
  StringSmall,
  StringXSmall,
} from '@components/Typography/Typography';
import cn from 'classnames';
import { motion } from 'framer-motion';

import { ResourceImage } from '../ResourceImage/ResourceImage';

import cashPng from '/img/icons/cash.png';
import cashWebp from '/img/icons/cash.webp';
import dicePng from '/img/icons/dice.png';
import diceWebp from '/img/icons/dice.webp';
import coinPng from '/img/icons/gems.png';
import coinWebp from '/img/icons/gems.webp';
import usdPng from '/img/icons/usd.png';
import usdWebp from '/img/icons/usd.webp';

interface ValueTagProps extends HTMLAttributes<HTMLDivElement> {
  value: number | string;
  kind: CurrencyType;
  variant: 'default' | 'balance' | 'huge' | 'reward';
  fontWeight?:
    | 'bold'
    | 'default'
    | 'regular'
    | 'medium'
    | 'semibold'
    | 'extrabold'
    | 'black';
  scale?: boolean;
}

const ValueTag = forwardRef<HTMLDivElement, ValueTagProps>(
  ({ value, kind, variant = 'default', fontWeight, scale, ...props }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);

    const styles: Record<
      ValueTagProps['variant'],
      { wrapper: string; image: string }
    > = {
      default: {
        wrapper: 'gap-1',
        image: 'w-6 h-6 shrink-0',
      },
      balance: {
        wrapper: 'gap-[2px]',
        image: 'w-[25.6px] h-[25.6px] shrink-0 relative',
      },
      huge: {
        wrapper: 'gap-1',
        image: 'w-8 h-8 shrink-0',
      },
      reward: {
        wrapper: 'gap-1',
        image: 'w-6 h-6 shrink-0',
      },
    };
    const images: Record<
      'tgem' | 'cash' | 'usdt' | 'dice',
      { png: string; webp: string }
    > = {
      tgem: {
        png: coinPng,
        webp: coinWebp,
      },
      cash: {
        png: cashPng,
        webp: cashWebp,
      },
      usdt: {
        png: usdPng,
        webp: usdWebp,
      },
      dice: {
        png: dicePng,
        webp: diceWebp,
      },
    };

    useImperativeHandle(ref, () => {
      const element = containerRef.current!;

      return Object.assign(element, {
        getImageElement: () => imageRef,
      });
    });

    const style = styles[variant];

    const currencyKinds = ['cash', 'tgem', 'usdt', 'dice'] as const;

    type CurrencyKind = (typeof currencyKinds)[number];
    const isCurrencyKind = (kind: string): kind is CurrencyKind => {
      return (currencyKinds as readonly string[]).includes(kind);
    };

    return (
      <div
        ref={containerRef}
        {...props}
        className={cn('flex items-center', style.wrapper, props.className)}
      >
        {isCurrencyKind(kind) ? (
          <motion.div
            className={style.image}
            ref={imageRef}
            variants={{
              scale: {
                filter: 'drop-shadow(0 0px 8px #82E024)',
                scale: 1.6,
              },
              default: {
                filter: 'drop-shadow(0 0px 0px #82E024)',
                scale: 1,
              },
            }}
            animate={scale ? 'scale' : 'default'}
            transition={{
              duration: 0.2,
              ease: 'easeInOut',
            }}
          >
            <picture>
              <source srcSet={images[kind].webp} type="image/webp" />
              <img src={images[kind].png} alt="task" />
            </picture>
            {kind === 'cash' && (
              <motion.div className="w-3 h-4 blur-sm bg-surface rounded-full absolute right-[6px] bottom-[4.8px] rotate-[34deg] -z-10" />
            )}
          </motion.div>
        ) : (
          <ResourceImage size={24} kind={kind} />
        )}
        {variant === 'default' && (
          <StringXSmall weight={fontWeight}>{value}</StringXSmall>
        )}
        {variant === 'balance' && (
          <StringSmall weight={fontWeight}>{value}</StringSmall>
        )}
        {variant === 'huge' && <H1 weight={fontWeight}>{value}</H1>}
        {variant === 'reward' && (
          <StringMain weight={fontWeight} className="text-white/60">
            {value}
          </StringMain>
        )}
      </div>
    );
  },
);

export { ValueTag };
